import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import pencilTokens from "@amzn/meridian-tokens/base/icon/pencil";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import {
  formatReportTimestamp,
  INFRASTRUCTURE_FAILURE_HINT,
  isInfrastructureFailure,
  regionShortLabel,
} from "../adminReportUtils";
import { FailedCase } from "../types";
import Chip from "./chip";
import { ReasonCodeChip } from "./documentChips";
import SortableHeader from "./sortableHeader";

interface Column {
  label: string;
  field?: keyof FailedCase;
}

const COLUMNS: Column[] = [
  { label: "Region", field: "region" },
  { label: "Test Case ID", field: "testCaseId" },
  { label: "Question", field: "question" },
  { label: "Expected Document", field: "expectedFileName" },
  { label: "Reason", field: "reasonCode" },
  { label: "Actually Cited" },
  { label: "Run Date", field: "lastRunAt" },
  { label: "" },
];

interface FailedCasesTableProps {
  failedCases: FailedCase[];
  sortColumn: keyof FailedCase;
  sortDirection: string;
  onSort: (field: keyof FailedCase) => void;
  onEdit: (failedCase: FailedCase) => void;
  onRemove: (failedCase: FailedCase) => void;
}

const FailedCasesTable = ({
  failedCases,
  sortColumn,
  sortDirection,
  onSort,
  onEdit,
  onRemove,
}: FailedCasesTableProps) => (
  <table className="admin-report__table">
    <thead>
      <tr>
        {COLUMNS.map(({ label, field }) => (
          <SortableHeader
            key={label || "actions"}
            label={label}
            field={field as string}
            sortColumn={sortColumn as string}
            sortDirection={sortDirection}
            onSort={onSort as (field: string) => void}
          />
        ))}
      </tr>
    </thead>
    <tbody>
      {failedCases.map((failedCase) => {
        const infrastructure = isInfrastructureFailure(failedCase.reasonCode);

        return (
          // attachmentId is the table's partition key and a file carries at
          // most one test case, so this is unique without the row index — and
          // stays stable when the user re-sorts.
          <tr key={`${failedCase.region}-${failedCase.attachmentId}`}>
            <td>
              <Chip tone="blue">{regionShortLabel(failedCase.region)}</Chip>
            </td>
            <td>
              <span className="admin-report__mono">
                {failedCase.testCaseId}
              </span>
            </td>
            <td>{failedCase.question}</td>
            <td>
              <span className="admin-report__doc-name">
                {failedCase.expectedFileName}
              </span>
            </td>
            <td>
              <ReasonCodeChip reasonCode={failedCase.reasonCode} />
            </td>
            <td>
              {failedCase.returnedTitles.length ? (
                <ul className="admin-report__titles">
                  {failedCase.returnedTitles.map((title, index) => (
                    <li key={`${index}-${title}`}>{title}</li>
                  ))}
                </ul>
              ) : (
                <span className="admin-report__doc-sub">Nothing cited</span>
              )}
            </td>
            <td>
              <span className="admin-report__doc-sub">
                {formatReportTimestamp(failedCase.lastRunAt) || "—"}
              </span>
            </td>
            <td>
              {infrastructure ? (
                <span className="admin-report__hint">
                  {INFRASTRUCTURE_FAILURE_HINT}
                </span>
              ) : (
                <div className="admin-report__row-actions">
                  <Button
                    type="icon"
                    size="small"
                    aria-label={`Edit ${failedCase.testCaseId}`}
                    onClick={() => onEdit(failedCase)}
                  >
                    <Icon tokens={pencilTokens} />
                  </Button>
                  <Button
                    type="icon"
                    size="small"
                    aria-label={`Remove ${failedCase.testCaseId}`}
                    onClick={() => onRemove(failedCase)}
                  >
                    <Icon tokens={trashTokens} />
                  </Button>
                </div>
              )}
            </td>
          </tr>
        );
      })}
      {!failedCases.length && (
        <tr>
          <td className="admin-report__table-empty" colSpan={COLUMNS.length}>
            No failing test cases match the current filters.
          </td>
        </tr>
      )}
    </tbody>
  </table>
);

export default FailedCasesTable;
